import { Moon, Sun, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ThemeToggle() {
  const [dark, setDark] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    setMounted(true);
    const isDark = document.documentElement.classList.contains('dark');
    setDark(isDark);
  }, []);

  const toggleTheme = () => {
    const root = document.documentElement;
    root.classList.toggle('dark');
    setDark(!dark);
    localStorage.setItem('theme', dark ? 'light' : 'dark');
  };

  if (!mounted) {
    return (
      <Button 
        variant="ghost" 
        size="icon" 
        className="rounded-full"
        disabled
      >
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
      </Button>
    );
  }

  return (
    <motion.div
      whileHover="hover"
      onHoverStart={() => setIsHovering(true)}
      onHoverEnd={() => setIsHovering(false)}
      className="relative"
    >
      {/* Floating particles on hover */}
      <AnimatePresence>
        {isHovering && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            className="absolute -inset-2"
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                className={`absolute rounded-full ${
                  dark 
                    ? 'bg-yellow-400/20' 
                    : 'bg-blue-400/20'
                }`}
                style={{
                  width: Math.random() * 8 + 4,
                  height: Math.random() * 8 + 4,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  x: [0, Math.random() * 10 - 5, 0],
                  opacity: [0.2, 0.8, 0.2],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className={`relative rounded-full transition-all duration-300 ${
          dark 
            ? 'bg-gradient-to-br from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800' 
            : 'bg-gradient-to-br from-blue-50 to-cyan-50 hover:from-blue-100 hover:to-cyan-100'
        }`}
      >
        {/* Animated background glow */}
        <motion.div
          className={`absolute inset-0 rounded-full blur-md ${
            dark 
              ? 'bg-gradient-to-r from-yellow-400/10 to-orange-400/10' 
              : 'bg-gradient-to-r from-blue-400/10 to-purple-400/10'
          }`}
          animate={{
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Icon container with rotation */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={dark ? 'sun' : 'moon'}
              initial={{ 
                rotate: dark ? 90 : -90,
                scale: 0.5,
                opacity: 0 
              }}
              animate={{ 
                rotate: 0,
                scale: 1,
                opacity: 1 
              }}
              exit={{ 
                rotate: dark ? -90 : 90,
                scale: 0.5,
                opacity: 0 
              }}
              transition={{ 
                type: "spring",
                stiffness: 300,
                damping: 20 
              }}
              className="relative"
            >
              {/* Glow effect */}
              <motion.div
                className={`absolute inset-0 rounded-full blur-sm ${
                  dark 
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-400' 
                    : 'bg-gradient-to-r from-blue-400 to-purple-400'
                }`}
                animate={{
                  opacity: [0.1, 0.3, 0.1],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Icon */}
              {dark ? (
                <motion.div
                  animate={{ 
                    rotate: 360,
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    rotate: { 
                      duration: 20, 
                      repeat: Infinity, 
                      ease: "linear" 
                    },
                    scale: { 
                      duration: 3, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }
                  }}
                >
                  <Sun className="h-5 w-5 text-yellow-400 relative z-10" />
                </motion.div>
              ) : (
                <motion.div
                  animate={{ 
                    rotate: 360,
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ 
                    rotate: { 
                      duration: 30, 
                      repeat: Infinity, 
                      ease: "linear" 
                    },
                    scale: { 
                      duration: 4, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }
                  }}
                >
                  <Moon className="h-5 w-5 text-blue-600 relative z-10" />
                </motion.div>
              )}

              {/* Sparkle effect on hover */}
              <AnimatePresence>
                {isHovering && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute -top-1 -right-1"
                  >
                    <Sparkles className={`h-3 w-3 ${
                      dark ? 'text-yellow-300' : 'text-blue-400'
                    }`} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Pulse ring effect */}
        <motion.div
          className={`absolute inset-0 rounded-full border ${
            dark ? 'border-yellow-400/30' : 'border-blue-400/30'
          }`}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.5, 0, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </Button>

      {/* Subtle tooltip text on hover */}
      <AnimatePresence>
        {isHovering && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
          >
            <div className={`px-2 py-1 rounded-md text-xs font-medium ${
              dark 
                ? 'bg-gray-800 text-yellow-300' 
                : 'bg-white text-blue-600 shadow-sm'
            }`}>
              Switch to {dark ? 'Light' : 'Dark'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}